<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import Avatar from 'next/avatar/Avatar.vue';

const props = defineProps({
  contact: {
    type: Object,
    default: () => ({}),
  },
});

const route = useRoute();

const additional = computed(() => props.contact.additional_attributes || {});

const location = computed(() => {
  const { city, country } = additional.value;
  return [city, country].filter(Boolean).join(', ');
});

const visitCount = computed(() => {
  const history = additional.value.visit_history;
  return Array.isArray(history) ? history.length : 0;
});

const rows = computed(() =>
  [
    {
      key: 'email',
      icon: 'i-lucide-mail',
      value: props.contact.email,
      href: props.contact.email ? `mailto:${props.contact.email}` : '',
    },
    {
      key: 'phone',
      icon: 'i-lucide-phone',
      value: props.contact.phone_number,
      href: props.contact.phone_number
        ? `tel:${props.contact.phone_number}`
        : '',
    },
    {
      key: 'company',
      icon: 'i-lucide-building-2',
      value: additional.value.company_name,
    },
    {
      key: 'location',
      icon: 'i-lucide-map-pin',
      value: location.value,
    },
  ].filter(row => row.value)
);

const profilePath = computed(
  () => `/app/accounts/${route.params.accountId}/contacts/${props.contact.id}`
);
</script>

<template>
  <div class="flex flex-col gap-3 px-4 py-4 border-b border-n-weak">
    <div class="flex items-start gap-3">
      <Avatar :src="contact.thumbnail" :name="contact.name" :size="40" />
      <div class="flex flex-col min-w-0 gap-1">
        <h2
          class="mb-0 text-base font-medium leading-tight text-n-slate-12 truncate"
        >
          {{ contact.name }}
        </h2>
        <span
          v-if="visitCount"
          class="self-start px-1.5 py-0.5 text-xs rounded bg-n-alpha-2 text-n-slate-11"
        >
          {{
            visitCount > 1
              ? $t('CONVERSATION_SIDEBAR.LEAD_PANEL.RETURNING_VISITOR', {
                  count: visitCount,
                })
              : $t('CONVERSATION_SIDEBAR.LEAD_PANEL.FIRST_VISIT')
          }}
        </span>
      </div>
    </div>
    <div v-if="rows.length" class="flex flex-col gap-1.5">
      <div
        v-for="row in rows"
        :key="row.key"
        class="flex items-center gap-2 min-w-0"
      >
        <span :class="row.icon" class="size-3.5 shrink-0 text-n-slate-10" />
        <a
          v-if="row.href"
          :href="row.href"
          class="text-sm truncate text-n-slate-11 hover:text-n-brand"
        >
          {{ row.value }}
        </a>
        <span v-else class="text-sm truncate text-n-slate-11">
          {{ row.value }}
        </span>
      </div>
    </div>
    <router-link
      :to="profilePath"
      class="self-start text-xs text-n-brand hover:underline"
    >
      {{ $t('CONVERSATION_SIDEBAR.LEAD_PANEL.VIEW_PROFILE') }}
    </router-link>
  </div>
</template>
